/**
 * ScheduleAwareRecommendations
 * 
 * Phase 4: Schedule-aware recommendations display component.
 * Shows categorized recommendations: Help Now, Prep Upcoming, Skill Growth.
 */

import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Loader2, 
  BookOpen, 
  Zap, 
  Calendar, 
  TrendingUp,
  Info
} from 'lucide-react';
import { TrainingRecommendationCard } from './TrainingRecommendationCard';
import { useScheduleAwareRecommendations, type CategorizedRecommendations } from '@/hooks/useScheduleAwareRecommendations';
import { useTrackLearningClick } from '@/hooks/useLearningCenter';
import type { TrainingArtifact, LearningIntent } from '@/types/training';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ScheduleAwareRecommendationsProps {
  technicianProfileId: string;
  canOpen?: boolean;
}

export function ScheduleAwareRecommendations({
  technicianProfileId,
  canOpen = true,
}: ScheduleAwareRecommendationsProps) {
  const { data, isLoading, error } = useScheduleAwareRecommendations({
    technicianProfileId,
  });
  const trackClick = useTrackLearningClick();

  const handleArtifactClick = (artifact: TrainingArtifact, intent?: LearningIntent) => {
    trackClick.mutate({
      technicianProfileId,
      artifactId: artifact.id,
      source: 'learning_center',
      intent: intent || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>Failed to load recommendations</p>
      </div>
    );
  }

  const recommendations = data?.recommendations;
  const context = data?.scheduleContext;

  const isEmpty = 
    !recommendations ||
    (recommendations.helpNow.length === 0 &&
     recommendations.prepUpcoming.length === 0 &&
     recommendations.skillGrowth.length === 0);

  if (isEmpty) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>No personalized recommendations yet.</p>
        <p className="text-sm mt-1">
          {context?.ticketCount === 0
            ? 'No tickets scheduled in your window.'
            : 'Check back as your schedule builds.'}
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-6">
        {/* Schedule Context Badge */}
        {context && context.ticketCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <Info className="h-3 w-3" />
                    <span>Based on {context.ticketCount} tickets in your schedule</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">
                    Looking at {context.dateRange.start} to {context.dateRange.end}.
                    {context.jobCategories.length > 0 && (
                      <span className="block mt-1">
                        Categories: {context.jobCategories.slice(0, 3).join(', ')}
                        {context.jobCategories.length > 3 && '...'}
                      </span>
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* Help Now Section */}
        {recommendations.helpNow.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-4 w-4 text-amber-500" />
              <h3 className="font-semibold text-sm">Help Now</h3>
              <Badge variant="secondary" className="text-xs">
                Today's jobs
              </Badge>
            </div>
            <div className="space-y-3">
              {recommendations.helpNow.map((scored) => (
                <TrainingRecommendationCard
                  key={scored.artifact.id}
                  artifact={scored.artifact as any}
                  whyShowing={scored.explanation}
                  onOpen={(intent) => handleArtifactClick(scored.artifact as any, intent)}
                  showActions={false}
                  canOpen={canOpen}
                />
              ))}
            </div>
          </section>
        )}

        {/* Prep Upcoming Section */}
        {recommendations.prepUpcoming.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-blue-500" />
              <h3 className="font-semibold text-sm">Prep for Upcoming</h3>
              <Badge variant="outline" className="text-xs">
                Next few days
              </Badge>
            </div>
            <div className="space-y-3">
              {recommendations.prepUpcoming.map((scored) => (
                <TrainingRecommendationCard
                  key={scored.artifact.id}
                  artifact={scored.artifact as any}
                  whyShowing={scored.explanation}
                  onOpen={(intent) => handleArtifactClick(scored.artifact as any, intent)}
                  showActions={false}
                  canOpen={canOpen}
                />
              ))}
            </div>
          </section>
        )}

        {/* Skill Growth Section */}
        {recommendations.skillGrowth.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <h3 className="font-semibold text-sm">Skill Growth</h3>
              <Badge variant="outline" className="text-xs">
                Foundation
              </Badge>
            </div>
            <div className="space-y-3">
              {recommendations.skillGrowth.map((scored) => (
                <TrainingRecommendationCard
                  key={scored.artifact.id}
                  artifact={scored.artifact as any}
                  whyShowing={scored.explanation}
                  onOpen={(intent) => handleArtifactClick(scored.artifact as any, intent)}
                  showActions={false}
                  canOpen={canOpen}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </ScrollArea>
  );
}
